/* project-planning.jsx — Design phase surface (Tickets moved to the Board tab).
   ─────────────────────────────────────────────────────────────────────────────
   Drop-in replacement for the old project-planning.jsx. The editable ticket draft
   now lives in useProjectPlanning() (project-planning-state.jsx) and is passed in
   as `planning`, shared with <ProjectBoardTab>. This file renders the design
   phase rail:
       Work package/section selector · Sketches · Scope concepts · Tech stack
       · Guidelines · Milestones.

   Signature (called from project-detail.jsx):
     <ProjectPlanningExec planning={planning} project={project} db={db}
        onDrawer={onDrawer} onAction={onAction} DocumentsPanel={DocumentsPanel} Inline={Inline} />

   Milestones persist immediately (unchanged contract):
     toggle  → onAction('toggleProjectMilestone',  { project, milestoneId })
     reorder → onAction('reorderProjectMilestone', { project, milestoneId, direction })
     delete  → onAction('deleteProjectMilestone',  { ...milestone, projectId })
     edit    → onDrawer('projectMilestone', milestoneId, { projectId })
     add     → onDrawer('projectMilestone', null, { projectId, workPackageId })

   Scope concepts persist as OKF knowledge concepts. Section names live in
   the shared draft and save via the planning Save bar (rendered by the shell).
   Requires Icon, Panel, Pill (ui.jsx) and the
   pd-/ms-/ring styles already in project-detail.css.
   Exports: window.ProjectPlanningExec
   ───────────────────────────────────────────────────────────────────────────── */
(function () {
  const { useEffect, useState } = React;
  const { money, fmtDate, fmtDateTime, TODAY } = window.AC;
  const MODEL = window.MODEL;
  const decimal = (v) => { const n = Number(v); return Number.isFinite(n) ? n.toString() : '0'; };
  const milestoneEstimatedCost = (m) => (Number(m.estimatedHours) || 0) * (Number(m.hourlyRate) || 0);
  const milestoneInvoiceAmount = (m) => (Number(m.actualCost) || 0) || ((Number(m.actualHours) || 0) * (Number(m.hourlyRate) || 0)) || milestoneEstimatedCost(m);
  const today = (MODEL && MODEL.TODAY) || TODAY;

  /* progress ring — two circles only (reuses window.ProgressRing if present) */
  function Ring({ value, size = 104, stroke = 9 }) {
    if (window.ProgressRing) return <window.ProgressRing value={value} size={size} label="done" />;
    const r = (size - stroke) / 2, c = 2 * Math.PI * r, pct = Math.max(0, Math.min(1, value));
    return (
      <div className="ring" style={{ width: size, height: size }}>
        <svg width={size} height={size}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--surface-3)" strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={pct >= 1 ? 'var(--pos)' : 'var(--primary)'} strokeWidth={stroke}
            strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct)}
            transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset .4s ease' }} />
        </svg>
        <div className="ring-center"><div className="ring-pct" style={{ fontSize: size * 0.26 }}>{Math.round(pct * 100)}%</div><div className="ring-sub">done</div></div>
      </div>
    );
  }

  function scopeSlug(value) {
    return String(value || 'scope-concept')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'scope-concept';
  }

  function scopeConceptPath(item) {
    const path = String(item && item.path || '').trim().replace(/^\/+|\/+$/g, '');
    if (path) return path;
    const section = scopeSlug(item && item.sectionName);
    return `scope/${section}/${scopeSlug(item && item.title)}.md`;
  }

  function buildScopeTree(items) {
    const root = { name: 'OKF bundle', path: '', folders: new Map(), files: [] };
    (items || []).forEach((item) => {
      const path = scopeConceptPath(item);
      const parts = path.split('/').filter(Boolean);
      const fileName = parts.pop() || `${scopeSlug(item.title)}.md`;
      let cursor = root;
      parts.forEach((part) => {
        if (!cursor.folders.has(part)) {
          const nextPath = [cursor.path, part].filter(Boolean).join('/');
          cursor.folders.set(part, { name: part, path: nextPath, folders: new Map(), files: [] });
        }
        cursor = cursor.folders.get(part);
      });
      cursor.files.push({ ...item, fileName, okfPath: path });
    });
    const sortNode = (node) => ({
      ...node,
      folders: Array.from(node.folders.values()).sort((a, b) => a.name.localeCompare(b.name)).map(sortNode),
      files: node.files.sort((a, b) => a.fileName.localeCompare(b.fileName)),
    });
    return sortNode(root);
  }

  function ScopeTreeNode({ node, selectedId, onSelect, depth = 0 }) {
    const [open, setOpen] = useState(true);
    const folders = node.folders || [];
    const files = node.files || [];
    const expanded = depth === 0 || open;
    return (
      <div className="okf-tree-node" style={{ '--depth': depth }}>
        {depth > 0 && (
          <button className="okf-folder-row" onClick={() => setOpen((value) => !value)} title={open ? 'Collapse folder' : 'Expand folder'}>
            <span><Icon name={open ? 'chevD' : 'chevR'} size={13} /></span>
            <strong>{node.name}</strong>
          </button>
        )}
        {expanded && folders.map((folder) => <ScopeTreeNode key={folder.path} node={folder} selectedId={selectedId} onSelect={onSelect} depth={depth + 1} />)}
        {expanded && files.map((file) => (
          <button key={file.id} className={'okf-file-row' + (selectedId === file.id ? ' on' : '')} style={{ '--depth': depth + 1 }} onClick={() => onSelect(file.id)}>
            <span><Icon name="doc" size={14} /></span>
            <strong>{file.title || file.fileName}</strong>
            <small>{file.fileName}</small>
          </button>
        ))}
      </div>
    );
  }

  function okfConceptType(item) {
    return String(item && (item.conceptType || item.type) || '').trim() || 'Concept';
  }

  function okfConceptSearchText(item) {
    return [
      item && item.title,
      item && item.path,
      item && item.description,
      item && (item.bodyMarkdown || item.text),
    ].filter(Boolean).join(' ').toLowerCase();
  }

  function isRequirementConcept(item) {
    return okfConceptType(item).toLowerCase() === 'requirement';
  }

  function okfConceptPayload(item, patch = {}) {
    const nextMetadata = { ...(item.metadata || {}) };
    if (Object.prototype.hasOwnProperty.call(patch, 'type') && isRequirementConcept(item)) nextMetadata.requirementType = patch.type;
    return {
      workPackageId: Object.prototype.hasOwnProperty.call(patch, 'workPackageId') ? patch.workPackageId || null : item.workPackageId || null,
      path: Object.prototype.hasOwnProperty.call(patch, 'path') ? patch.path || '' : item.path || '',
      type: Object.prototype.hasOwnProperty.call(patch, 'conceptType') ? patch.conceptType || okfConceptType(item) : okfConceptType(item),
      title: Object.prototype.hasOwnProperty.call(patch, 'title') ? patch.title || '' : item.title || '',
      description: Object.prototype.hasOwnProperty.call(patch, 'description') ? patch.description || '' : item.description || '',
      bodyMarkdown: Object.prototype.hasOwnProperty.call(patch, 'text') ? patch.text || '' : item.bodyMarkdown || item.text || '',
      status: Object.prototype.hasOwnProperty.call(patch, 'status') ? window.AC.cleanProjectRequirementStatus(patch.status) : window.AC.cleanProjectRequirementStatus(item.status),
      priority: Object.prototype.hasOwnProperty.call(patch, 'priority') ? window.AC.cleanProjectRequirementPriority(patch.priority) : window.AC.cleanProjectRequirementPriority(item.priority),
      tags: Object.prototype.hasOwnProperty.call(patch, 'tags') ? (Array.isArray(patch.tags) ? patch.tags : []) : Array.isArray(item.tags) ? item.tags : [],
      metadata: nextMetadata,
    };
  }

  function okfConnectionLabel(kind, id, conceptsById) {
    const concept = conceptsById && conceptsById.get && conceptsById.get(id);
    if (concept) return concept.title || concept.path || id;
    return String(id || '').slice(0, 8);
  }

  function RequirementsPanel({ active, planning, project, db, onDrawer, onAction, onToast, onError, onReload, onVoiceContextChange }) {
    const [listTab, setListTab] = useState('screenDrafts');
    const [railExpanded, setRailExpanded] = useState(false);
    const [sketchZen, setSketchZen] = useState(false);
    const [screenDraftZen, setScreenDraftZen] = useState(false);
    const [guidelineDrawerOpen, setGuidelineDrawerOpen] = useState(false);
    const [guidelineScope, setGuidelineScope] = useState('project');
    const [selectedGuidelineTemplateIds, setSelectedGuidelineTemplateIds] = useState([]);
    const [visualGuidelineDrawerOpen, setVisualGuidelineDrawerOpen] = useState(false);
    const [visualGuidelineScope, setVisualGuidelineScope] = useState('workPackage');
    const [visualGuidelineTemplateId, setVisualGuidelineTemplateId] = useState('');
    const [libraryTemplateDrawerOpen, setLibraryTemplateDrawerOpen] = useState(false);
    const [libraryTemplateScope, setLibraryTemplateScope] = useState('workPackage');
    const [libraryTemplateId, setLibraryTemplateId] = useState('');
    const [localGuidelineDrawer, setLocalGuidelineDrawer] = useState(null);
    const [selectedScopeId, setSelectedScopeId] = useState('');
    const [okfSearch, setOkfSearch] = useState('');
    const sections = Array.isArray(planning.draft) && planning.draft.length ? planning.draft : [active];
    const sectionOptions = sections.map((section) => ({ value: section.id, label: section.name || 'Work package' }));
    const items = (window.MODEL.projectKnowledgeConcepts ? window.MODEL.projectKnowledgeConcepts(project) : window.MODEL.projectRequirements(project));
    const activeItems = items.filter((item) => item.workPackageId === active.id || (!item.workPackageId && active.position === 0));
    const titled = activeItems.filter((item) => item.title || item.path);
    const okfSearchQuery = okfSearch.trim().toLowerCase();
    const filteredTitled = okfSearchQuery ? titled.filter((item) => okfConceptSearchText(item).includes(okfSearchQuery)) : titled;
    const openItem = (item) => {
      onDrawer('projectRequirements', item && item.id, {
        workPackageId: (item && item.workPackageId) || active.id,
        workPackages: sections.map((section) => ({ id: section.id, name: section.name })),
        requirementItem: item,
        onApply: async (form) => {
          if (planning.dirty) await planning.save();
          if (item && !isRequirementConcept(item)) {
            return onAction('updateProjectKnowledgeConcept', {
              projectId: project.id,
              conceptId: item.id,
              concept: okfConceptPayload(item, {
                workPackageId: form.workPackageId,
                path: form.path,
                conceptType: form.conceptType,
                title: form.title,
                description: form.description,
                text: form.text,
                priority: form.priority,
                status: form.status,
                tags: String(form.tagsText || '').split(',').map((tag) => tag.trim()).filter(Boolean),
              }),
            });
          }
          return onAction(item ? 'updateProjectRequirement' : 'createProjectRequirement', {
            projectId: project.id,
            requirementId: item && item.id,
            requirement: {
              workPackageId: form.workPackageId || active.id,
              title: form.title || '',
              text: form.text || '',
              type: window.AC.cleanProjectRequirementType(form.type),
              priority: window.AC.cleanProjectRequirementPriority(form.priority),
              status: window.AC.cleanProjectRequirementStatus(form.status),
            },
          });
        },
      });
    };
    const updateItem = async (item, patch) => {
      if (planning.dirty) await planning.save();
      if (!isRequirementConcept(item)) {
        return onAction('updateProjectKnowledgeConcept', {
          projectId: project.id,
          conceptId: item.id,
          concept: okfConceptPayload(item, patch),
        });
      }
      return onAction('updateProjectRequirement', {
        projectId: project.id,
        requirementId: item.id,
        requirement: {
          workPackageId: item.workPackageId || active.id,
          title: item.title || '',
          text: item.text || '',
          type: window.AC.cleanProjectRequirementType(item.type),
          priority: window.AC.cleanProjectRequirementPriority(item.priority),
          status: window.AC.cleanProjectRequirementStatus(item.status),
          ...patch,
        },
      });
    };
    const removeItem = (item) => isRequirementConcept(item) ? onAction('deleteProjectRequirement', {
        projectId: project.id,
        workPackageId: item.workPackageId,
        requirementId: item.id,
        title: item.title,
      }) : onAction('deleteProjectKnowledgeConcept', {
        projectId: project.id,
        conceptId: item.id,
        title: item.title,
      });
    const techItems = (project.techStackItems || []).slice().sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.tech || '').localeCompare(String(b.tech || '')));
    const openTechItem = (item) => {
      onDrawer('projectTechStack', item && item.id, {
        techStackItem: item,
        onApply: async (form) => {
          if (planning.dirty) await planning.save();
          return onAction(item ? 'updateProjectTechStackItem' : 'createProjectTechStackItem', {
            projectId: project.id,
            itemId: item && item.id,
            item: {
              tech: form.tech || '',
              category: window.AC.cleanProjectTechStackCategory(form.category),
            },
          });
        },
      });
    };
    const removeTechItem = (item) => onAction('deleteProjectTechStackItem', {
      projectId: project.id,
      itemId: item.id,
      tech: item.tech,
    });
    const savedActive = (project.workPackages || []).find((workPackage) => workPackage.id === active.id) || active;
    const guidelineTemplates = (db.implementationGuidelineTemplates || []).slice().sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    const linkedProjectGuidelineIds = project.implementationGuidelineTemplateIds || [];
    const linkedWorkPackageGuidelineIds = savedActive.implementationGuidelineTemplateIds || [];
    const linkedProjectGuidelines = linkedProjectGuidelineIds
      .map((id) => guidelineTemplates.find((template) => template.id === id))
      .filter(Boolean);
    const linkedWorkPackageGuidelines = linkedWorkPackageGuidelineIds
      .map((id) => guidelineTemplates.find((template) => template.id === id))
      .filter(Boolean);
    const availableProjectGuidelines = guidelineTemplates.filter((template) => !linkedProjectGuidelineIds.includes(template.id));
    const availableWorkPackageGuidelines = guidelineTemplates.filter((template) => !linkedWorkPackageGuidelineIds.includes(template.id));
    const availableGuidelines = guidelineScope === 'workPackage' ? availableWorkPackageGuidelines : availableProjectGuidelines;
    const saveProjectGuidelineLinks = async (templateIds) => {
      if (planning.dirty) await planning.save();
      return onAction('updateProjectGuidelineTemplates', {
        projectId: project.id,
        templateIds,
      });
    };
    const saveWorkPackageGuidelineLinks = async (templateIds) => {
      if (planning.dirty) await planning.save();
      return onAction('updateProjectWorkPackageImplementationGuidelineTemplates', {
        projectId: project.id,
        workPackageId: active.id,
        templateIds,
      });
    };
    const openGuidelineDrawer = (scope) => {
      const nextAvailable = scope === 'workPackage' ? availableWorkPackageGuidelines : availableProjectGuidelines;
      setGuidelineScope(scope);
      setSelectedGuidelineTemplateIds(nextAvailable.map((template) => template.id));
      setGuidelineDrawerOpen(true);
    };
    const closeGuidelineDrawer = () => {
      setGuidelineDrawerOpen(false);
      setSelectedGuidelineTemplateIds([]);
    };
    const toggleGuidelineSelection = (templateId) => {
      setSelectedGuidelineTemplateIds((ids) => ids.includes(templateId)
        ? ids.filter((id) => id !== templateId)
        : [...ids, templateId]);
    };
    const importGuidelines = async () => {
      const linkedIds = guidelineScope === 'workPackage' ? linkedWorkPackageGuidelineIds : linkedProjectGuidelineIds;
      const importIds = selectedGuidelineTemplateIds.filter((id) => !linkedIds.includes(id));
      if (!importIds.length) return;
      const ok = guidelineScope === 'workPackage'
        ? await saveWorkPackageGuidelineLinks([...linkedWorkPackageGuidelineIds, ...importIds])
        : await saveProjectGuidelineLinks([...linkedProjectGuidelineIds, ...importIds]);
      if (ok !== false) closeGuidelineDrawer();
    };
    const unlinkProjectGuideline = (template) => {
      if (!window.confirm(`Remove ${template.title || 'this guideline template'} from this project?`)) return;
      saveProjectGuidelineLinks(linkedProjectGuidelineIds.filter((id) => id !== template.id));
    };
    const unlinkWorkPackageGuideline = (template) => {
      if (!window.confirm(`Remove ${template.title || 'this guideline template'} from this work package?`)) return;
      saveWorkPackageGuidelineLinks(linkedWorkPackageGuidelineIds.filter((id) => id !== template.id));
    };
    const sketches = (project.sketches || [])
      .filter((sketch) => sketch.workPackageId === active.id)
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.title || '').localeCompare(String(b.title || '')));
    const screenDrafts = (project.screenDrafts || [])
      .filter((draft) => draft.workPackageId === active.id)
      .slice()
      .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.title || '').localeCompare(String(b.title || '')));
    const ScreenDraftsPanel = window.ProjectScreenDraftsPanel || window.ProjectScreenDraftPanel;
    const handleScreenDraftAction = async (action, payload = {}) => {
      const flash = (message) => { if (onToast) onToast(message); };
      const reload = async () => { if (onReload) await onReload(); };
      try {
        if (action === 'createProjectScreenDraft') {
          if (planning.dirty) await planning.save();
          const saved = await window.API.createProjectScreenDraft(payload.projectId || project.id, payload.screenDraft || payload.draft || {});
          flash('Screen draft created');
          await reload();
          return saved || true;
        }
        if (action === 'updateProjectScreenDraft') {
          if (planning.dirty) await planning.save();
          const draftId = payload.screenDraftId || payload.draftId;
          const saved = await window.API.updateProjectScreenDraft(payload.projectId || project.id, draftId, payload.screenDraft || payload.draft || {});
          flash(payload.silent ? 'Screen draft saved' : 'Screen draft updated');
          await reload();
          return saved || true;
        }
        if (action === 'deleteProjectScreenDraft') {
          const draftId = payload.screenDraftId || payload.draftId;
          if (!window.confirm(`Delete ${payload.title || 'this screen draft'}?`)) return false;
          await window.API.deleteProjectScreenDraft(payload.projectId || project.id, draftId);
          flash('Screen draft deleted');
          await reload();
          return true;
        }
        if (action === 'reorderProjectScreenDrafts') {
          await window.API.reorderProjectScreenDrafts(payload.projectId || project.id, payload.draftIds || payload.screenDraftIds || []);
          flash('Screen drafts reordered');
          await reload();
          return true;
        }
        if (action === 'reorderProjectScreenDraft') {
          const draftId = payload.screenDraftId || payload.draftId;
          const current = screenDrafts.find((item) => item.id === draftId);
          if (!current) return false;
          const from = screenDrafts.findIndex((item) => item.id === draftId);
          const to = from + payload.direction;
          if (from < 0 || to < 0 || to >= screenDrafts.length) return false;
          const ordered = screenDrafts.map((item) => item.id);
          [ordered[from], ordered[to]] = [ordered[to], ordered[from]];
          await window.API.reorderProjectScreenDrafts(payload.projectId || project.id, ordered);
          flash('Screen drafts reordered');
          await reload();
          return true;
        }
        return onAction(action, payload);
      } catch (error) {
        if (onError) onError(error);
        return false;
      }
    };
    const designTemplates = (db.projectDesignGuidelineTemplates || []).slice().sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    const linkedProjectDesignGuidelineIds = project.designGuidelineTemplateIds || [];
    const linkedWorkPackageDesignGuidelineIds = savedActive.designGuidelineTemplateIds || [];
    const projectDesignGuidelines = linkedProjectDesignGuidelineIds
      .map((templateId, index) => {
        const template = designTemplates.find((item) => item.id === templateId);
        return template ? { ...template, position: index } : null;
      })
      .filter(Boolean);
    const workPackageDesignGuidelines = linkedWorkPackageDesignGuidelineIds
      .map((templateId, index) => {
        const template = designTemplates.find((item) => item.id === templateId);
        return template ? { ...template, workPackageId: active.id, position: index } : null;
      })
      .filter(Boolean);
    const availableProjectDesignTemplates = designTemplates.filter((template) => !linkedProjectDesignGuidelineIds.includes(template.id));
    const availableWorkPackageDesignTemplates = designTemplates.filter((template) => !linkedWorkPackageDesignGuidelineIds.includes(template.id));
    const availableDesignTemplates = visualGuidelineScope === 'project' ? availableProjectDesignTemplates : availableWorkPackageDesignTemplates;
    const saveProjectDesignGuidelineLinks = async (templateIds) => {
      if (planning.dirty) await planning.save();
      return onAction('updateProjectDesignGuidelineTemplates', {
        projectId: project.id,
        templateIds,
      });
    };
    const saveWorkPackageDesignGuidelineLinks = async (templateIds) => {
      if (planning.dirty) await planning.save();
      return onAction('updateProjectWorkPackageDesignGuidelineTemplates', {
        projectId: project.id,
        workPackageId: active.id,
        templateIds,
      });
    };
    const openVisualGuidelineDrawer = (scope) => {
      const nextAvailable = scope === 'project' ? availableProjectDesignTemplates : availableWorkPackageDesignTemplates;
      setVisualGuidelineScope(scope);
      setVisualGuidelineTemplateId((nextAvailable[0] && nextAvailable[0].id) || '');
      setVisualGuidelineDrawerOpen(true);
    };
    const closeVisualGuidelineDrawer = () => {
      setVisualGuidelineDrawerOpen(false);
      setVisualGuidelineTemplateId('');
    };
    const linkVisualGuideline = async () => {
      const linkedIds = visualGuidelineScope === 'project' ? linkedProjectDesignGuidelineIds : linkedWorkPackageDesignGuidelineIds;
      if (!visualGuidelineTemplateId || linkedIds.includes(visualGuidelineTemplateId)) return;
      const ok = visualGuidelineScope === 'project'
        ? await saveProjectDesignGuidelineLinks([...linkedProjectDesignGuidelineIds, visualGuidelineTemplateId])
        : await saveWorkPackageDesignGuidelineLinks([...linkedWorkPackageDesignGuidelineIds, visualGuidelineTemplateId]);
      if (ok !== false) closeVisualGuidelineDrawer();
    };
    const unlinkProjectVisualGuideline = (template) => {
      if (!window.confirm(`Remove ${template.title || 'this visual guideline template'} from this project?`)) return;
      saveProjectDesignGuidelineLinks(linkedProjectDesignGuidelineIds.filter((id) => id !== template.id));
    };
    const unlinkWorkPackageVisualGuideline = (template) => {
      if (!window.confirm(`Remove ${template.title || 'this visual guideline template'} from this work package?`)) return;
      saveWorkPackageDesignGuidelineLinks(linkedWorkPackageDesignGuidelineIds.filter((id) => id !== template.id));
    };
    const libraryTemplates = (db.excalidrawLibraryTemplates || []).slice().sort((a, b) => String(a.title || '').localeCompare(String(b.title || '')));
    const linkedProjectLibraryIds = project.excalidrawLibraryTemplateIds || [];
    const linkedWorkPackageLibraryIds = savedActive.excalidrawLibraryTemplateIds || [];
    const projectLibraryTemplates = linkedProjectLibraryIds
      .map((templateId, index) => {
        const template = libraryTemplates.find((item) => item.id === templateId);
        return template ? { ...template, body: template.description || template.source || '', position: index } : null;
      })
      .filter(Boolean);
    const workPackageLibraryTemplates = linkedWorkPackageLibraryIds
      .map((templateId, index) => {
        const template = libraryTemplates.find((item) => item.id === templateId);
        return template ? { ...template, body: template.description || template.source || '', workPackageId: active.id, position: index } : null;
      })
      .filter(Boolean);
    const availableProjectLibraryTemplates = libraryTemplates.filter((template) => !linkedProjectLibraryIds.includes(template.id));
    const availableWorkPackageLibraryTemplates = libraryTemplates.filter((template) => !linkedWorkPackageLibraryIds.includes(template.id));
    const availableLibraryTemplates = libraryTemplateScope === 'project' ? availableProjectLibraryTemplates : availableWorkPackageLibraryTemplates;
    const saveProjectLibraryLinks = async (templateIds) => {
      if (planning.dirty) await planning.save();
      return onAction('updateProjectExcalidrawLibraryTemplates', {
        projectId: project.id,
        templateIds,
      });
    };
    const saveWorkPackageLibraryLinks = async (templateIds) => {
      if (planning.dirty) await planning.save();
      return onAction('updateProjectWorkPackageExcalidrawLibraryTemplates', {
        projectId: project.id,
        workPackageId: active.id,
        templateIds,
      });
    };
    const openLibraryTemplateDrawer = (scope) => {
      const nextAvailable = scope === 'project' ? availableProjectLibraryTemplates : availableWorkPackageLibraryTemplates;
      setLibraryTemplateScope(scope);
      setLibraryTemplateId((nextAvailable[0] && nextAvailable[0].id) || '');
      setLibraryTemplateDrawerOpen(true);
    };
    const closeLibraryTemplateDrawer = () => {
      setLibraryTemplateDrawerOpen(false);
      setLibraryTemplateId('');
    };
    const linkLibraryTemplate = async () => {
      const linkedIds = libraryTemplateScope === 'project' ? linkedProjectLibraryIds : linkedWorkPackageLibraryIds;
      if (!libraryTemplateId || linkedIds.includes(libraryTemplateId)) return;
      const ok = libraryTemplateScope === 'project'
        ? await saveProjectLibraryLinks([...linkedProjectLibraryIds, libraryTemplateId])
        : await saveWorkPackageLibraryLinks([...linkedWorkPackageLibraryIds, libraryTemplateId]);
      if (ok !== false) closeLibraryTemplateDrawer();
    };
    const unlinkProjectLibraryTemplate = (template) => {
      if (!window.confirm(`Remove ${template.title || 'this sketch library template'} from this project?`)) return;
      saveProjectLibraryLinks(linkedProjectLibraryIds.filter((id) => id !== template.id));
    };
    const unlinkWorkPackageLibraryTemplate = (template) => {
      if (!window.confirm(`Remove ${template.title || 'this sketch library template'} from this work package?`)) return;
      saveWorkPackageLibraryLinks(linkedWorkPackageLibraryIds.filter((id) => id !== template.id));
    };
    const localProjectGuidelines = (project.implementationGuidelines || []).slice().sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.title || '').localeCompare(String(b.title || '')));
    const localWorkPackageGuidelines = (savedActive.implementationGuidelines || []).slice().sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.title || '').localeCompare(String(b.title || '')));
    const localProjectDesignGuidelines = (project.designGuidelines || []).slice().sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.title || '').localeCompare(String(b.title || '')));
    const localWorkPackageDesignGuidelines = (savedActive.designGuidelines || []).slice().sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.title || '').localeCompare(String(b.title || '')));
    const openLocalGuidelineDrawer = (kind, scope, guideline = null) => {
      setLocalGuidelineDrawer({
        kind,
        scope,
        guideline,
        title: (guideline && guideline.title) || '',
        body: (guideline && guideline.body) || '',
      });
    };
    const closeLocalGuidelineDrawer = () => setLocalGuidelineDrawer(null);
    const setLocalGuidelineField = (field, value) => setLocalGuidelineDrawer((drawer) => drawer ? { ...drawer, [field]: value } : drawer);
    const saveLocalGuideline = async () => {
      if (!localGuidelineDrawer) return;
      if (planning.dirty) await planning.save();
      const scope = localGuidelineDrawer.scope;
      const isWorkPackage = scope === 'workPackage';
      const isUpdate = !!(localGuidelineDrawer.guideline && localGuidelineDrawer.guideline.id);
      const action = isWorkPackage
        ? (isUpdate ? 'updateProjectWorkPackageGuideline' : 'createProjectWorkPackageGuideline')
        : (isUpdate ? 'updateProjectGuideline' : 'createProjectGuideline');
      const ok = await onAction(action, {
        projectId: project.id,
        workPackageId: isWorkPackage ? active.id : null,
        guidelineId: localGuidelineDrawer.guideline && localGuidelineDrawer.guideline.id,
        kind: localGuidelineDrawer.kind,
        guideline: {
          title: localGuidelineDrawer.title || '',
          body: localGuidelineDrawer.body || '',
        },
      });
      if (ok !== false) closeLocalGuidelineDrawer();
    };
    const deleteLocalGuideline = (kind, scope, guideline) => onAction(scope === 'workPackage' ? 'deleteProjectWorkPackageGuideline' : 'deleteProjectGuideline', {
      projectId: project.id,
      workPackageId: scope === 'workPackage' ? active.id : null,
      guidelineId: guideline.id,
      kind,
      title: guideline.title,
    });
    const guidelineRows = [
      ...linkedProjectGuidelines.map((template) => ({ ...template, key: `technical-project-template-${template.id}`, kind: 'Technical', scope: 'Project', source: 'Template', onRemove: () => unlinkProjectGuideline(template) })),
      ...localProjectGuidelines.map((guideline) => ({ ...guideline, key: `technical-project-local-${guideline.id}`, kind: 'Technical', scope: 'Project', source: 'Project', onEdit: () => openLocalGuidelineDrawer('implementation', 'project', guideline), onRemove: () => deleteLocalGuideline('implementation', 'project', guideline) })),
      ...linkedWorkPackageGuidelines.map((template) => ({ ...template, key: `technical-workPackage-template-${template.id}`, kind: 'Technical', scope: active.name, source: 'Template', onRemove: () => unlinkWorkPackageGuideline(template) })),
      ...localWorkPackageGuidelines.map((guideline) => ({ ...guideline, key: `technical-workPackage-local-${guideline.id}`, kind: 'Technical', scope: active.name, source: 'Project', onEdit: () => openLocalGuidelineDrawer('implementation', 'workPackage', guideline), onRemove: () => deleteLocalGuideline('implementation', 'workPackage', guideline) })),
      ...projectDesignGuidelines.map((template) => ({ ...template, key: `visual-project-template-${template.id}`, kind: 'Visual', scope: 'Project', source: 'Template', onRemove: () => unlinkProjectVisualGuideline(template) })),
      ...localProjectDesignGuidelines.map((guideline) => ({ ...guideline, key: `visual-project-local-${guideline.id}`, kind: 'Visual', scope: 'Project', source: 'Project', onEdit: () => openLocalGuidelineDrawer('design', 'project', guideline), onRemove: () => deleteLocalGuideline('design', 'project', guideline) })),
      ...workPackageDesignGuidelines.map((template) => ({ ...template, key: `visual-workPackage-template-${template.id}`, kind: 'Visual', scope: active.name, source: 'Template', onRemove: () => unlinkWorkPackageVisualGuideline(template) })),
      ...localWorkPackageDesignGuidelines.map((guideline) => ({ ...guideline, key: `visual-workPackage-local-${guideline.id}`, kind: 'Visual', scope: active.name, source: 'Project', onEdit: () => openLocalGuidelineDrawer('design', 'workPackage', guideline), onRemove: () => deleteLocalGuideline('design', 'workPackage', guideline) })),
    ];
    const libraryRows = [
      ...projectLibraryTemplates.map((template) => ({ ...template, key: `library-project-template-${template.id}`, kind: 'Sketch library', scope: 'Project', source: template.source || 'Template', onRemove: () => unlinkProjectLibraryTemplate(template) })),
      ...workPackageLibraryTemplates.map((template) => ({ ...template, key: `library-workPackage-template-${template.id}`, kind: 'Sketch library', scope: active.name, source: template.source || 'Template', onRemove: () => unlinkWorkPackageLibraryTemplate(template) })),
    ];
    const milestones = (project.milestones || []).filter((m) => m.workPackageId === active.id || (!m.workPackageId && active.position === 0));
    const doneMilestones = milestones.filter((m) => m.completed).length;
    const ticketStatsByMilestone = planning.cats.reduce((stats, cat) => {
      cat.items.forEach((item) => {
        if (!item.milestoneId) return;
        const cur = stats[item.milestoneId] || { total: 0, done: 0, blocked: 0 };
        cur.total += 1;
        if (item.done) cur.done += 1;
        if (item.status === 'Blocked' || item.status === 'Missing info') cur.blocked += 1;
        stats[item.milestoneId] = cur;
      });
      return stats;
    }, {});
    const linkedTickets = milestones.reduce((acc, m) => {
      const stats = ticketStatsByMilestone[m.id] || { total: 0, done: 0, blocked: 0 };
      acc.total += stats.total;
      acc.done += stats.done;
      return acc;
    }, { total: 0, done: 0 });
    const reqCounters = titled.reduce((acc, item) => {
      const status = window.AC.cleanProjectRequirementStatus(item.status);
      if (status === 'accepted' || status === 'implemented') acc.signedOff += 1;
      else if (status === 'open question') acc.openQuestion += 1;
      else acc.draft += 1;
      return acc;
    }, { signedOff: 0, inReview: 0, draft: 0, openQuestion: 0 });
    const reqPct = titled.length ? Math.round((reqCounters.signedOff / titled.length) * 100) : 0;
    const conceptTypeCounts = titled.reduce((map, item) => {
      const type = okfConceptType(item);
      map.set(type, (map.get(type) || 0) + 1);
      return map;
    }, new Map());
    const conceptTypeChips = Array.from(conceptTypeCounts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).slice(0, 5);
    const scopeIds = filteredTitled.map((item) => item.id).join('|');
    const scopeTree = buildScopeTree(filteredTitled);
    const selectedScope = filteredTitled.find((item) => item.id === selectedScopeId) || filteredTitled[0] || null;
    const selectedScopePath = selectedScope ? scopeConceptPath(selectedScope) : '';
    const selectedScopeMarkdown = selectedScope ? (selectedScope.bodyMarkdown || selectedScope.text || selectedScope.description || '') : '';
    const selectedScopeSegments = selectedScopePath.split('/').filter(Boolean);
    const selectedIsRequirement = isRequirementConcept(selectedScope);
    const conceptsById = new Map(items.map((item) => [item.id, item]));
    const visibleConceptIds = new Set(filteredTitled.map((item) => item.id));
    const selectedSources = selectedScope ? (project.knowledgeSources || []).filter((source) => source.conceptId === selectedScope.id) : [];
    const selectedArtifacts = selectedScope ? (project.knowledgeArtifactLinks || []).filter((link) => link.conceptId === selectedScope.id) : [];
    const selectedOutgoingLinks = selectedScope ? (project.knowledgeLinks || []).filter((link) => link.fromConceptId === selectedScope.id) : [];
    const selectedIncomingLinks = selectedScope ? (project.knowledgeLinks || []).filter((link) => link.toConceptId === selectedScope.id) : [];
    const hasSelectedConnections = selectedSources.length || selectedArtifacts.length || selectedOutgoingLinks.length || selectedIncomingLinks.length;
    const categories = [
      { id: 'screenDrafts', label: 'Screen Drafts', icon: 'fileImage', sub: 'Structured product screens', count: screenDrafts.length },
      { id: 'sketches', label: 'Sketches', icon: 'flow', sub: 'Editable app canvas', count: sketches.length },
      { id: 'scope', label: 'OKF knowledge', icon: 'flag', sub: 'Requirements, decisions, constraints, entities', count: titled.length },
      { id: 'techStack', label: 'Tech stack', icon: 'stack', sub: 'Technology choices', count: techItems.length },
      { id: 'guidelines', label: 'Guidelines', icon: 'doc', sub: 'Technical and visual guidance', count: guidelineRows.length },
      { id: 'excalidrawLibraries', label: 'Excalidraw libraries', icon: 'fileImage', sub: 'Libraries available to sketch artist', count: libraryRows.length },
      { id: 'milestones', label: 'Milestones', icon: 'route', sub: 'Delivery checkpoints', count: milestones.length },
    ];
    const activeCategory = categories.find((item) => item.id === listTab) || categories[0];
    useEffect(() => {
      if (listTab !== 'sketches') setSketchZen(false);
    }, [listTab]);
    useEffect(() => {
      if (listTab !== 'screenDrafts') setScreenDraftZen(false);
    }, [listTab]);
    useEffect(() => {
      if (listTab !== 'scope') return;
      if (!filteredTitled.length) {
        if (selectedScopeId) setSelectedScopeId('');
        return;
      }
      if (!filteredTitled.some((item) => item.id === selectedScopeId)) setSelectedScopeId(filteredTitled[0].id);
    }, [listTab, selectedScopeId, scopeIds]);
    return (
      <div className={'req-stage' + (railExpanded ? ' rail-expanded' : ' rail-collapsed') + (listTab === 'sketches' || listTab === 'screenDrafts' ? ' sketch-active' : '') + (sketchZen || screenDraftZen ? ' sketch-zen' : '')}>
        <aside className="req-rail" aria-label="Design knowledge categories">
          <button className="req-rail-toggle" title={railExpanded ? 'Collapse section rail' : 'Expand section rail'} onClick={() => setRailExpanded((value) => !value)}>
            <span className="req-rail-ico"><Icon name={railExpanded ? 'chevL' : 'chevR'} size={15} /></span>
            <span className="req-rail-copy"><strong>{railExpanded ? 'Collapse' : 'Expand'}</strong><small>Section rail</small></span>
          </button>
          {categories.map((category) => (
            <button key={category.id} className={listTab === category.id ? 'on' : ''} title={category.label} onClick={() => { setListTab(category.id); if (category.id !== 'sketches') setSketchZen(false); if (category.id !== 'screenDrafts') setScreenDraftZen(false); }}>
              <span className="req-rail-ico"><Icon name={category.icon} size={15} /></span>
              <span className="req-rail-copy"><strong>{category.label}</strong><small>{category.sub}</small></span>
              <span className="pd-tabcount">{category.count}</span>
            </button>
          ))}
        </aside>
        <Panel title={listTab === 'sketches' || listTab === 'screenDrafts' ? null : activeCategory.label} sub={listTab === 'sketches' || listTab === 'screenDrafts' ? null : activeCategory.sub}
          className={listTab === 'sketches' || listTab === 'screenDrafts' ? 'sketch-shell-panel' : ''}
          flush={listTab === 'sketches' || listTab === 'screenDrafts'}
          actions={listTab === 'sketches' || listTab === 'screenDrafts' ? null : <React.Fragment>
            {listTab === 'scope' && (
              <React.Fragment>
                <label className="search okf-search">
                  <Icon name="search" size={14} />
                  <input value={okfSearch} onChange={(event) => setOkfSearch(event.target.value)} placeholder="Search OKF title or text..." aria-label="Search OKF title or text" />
                </label>
                <button className="btn sm ghost" onClick={() => openItem(null)}><Icon name="plus" size={14} />Scope concept</button>
              </React.Fragment>
            )}
            {listTab === 'techStack' && <button className="btn sm ghost" onClick={() => openTechItem(null)}><Icon name="plus" size={14} />Tech stack</button>}
            {listTab === 'guidelines' && <button className="btn sm ghost" onClick={() => openLocalGuidelineDrawer('implementation', 'project')}><Icon name="plus" size={14} />New tech project</button>}
            {listTab === 'guidelines' && <button className="btn sm ghost" onClick={() => openLocalGuidelineDrawer('implementation', 'workPackage')}><Icon name="plus" size={14} />New tech work package</button>}
            {listTab === 'guidelines' && <button className="btn sm ghost" onClick={() => openLocalGuidelineDrawer('design', 'project')}><Icon name="plus" size={14} />New visual project</button>}
            {listTab === 'guidelines' && <button className="btn sm ghost" onClick={() => openLocalGuidelineDrawer('design', 'workPackage')}><Icon name="plus" size={14} />New visual work package</button>}
            {listTab === 'guidelines' && <button className="btn sm ghost" disabled={availableProjectGuidelines.length === 0} title={availableProjectGuidelines.length === 0 ? 'All technical guideline templates are already linked to this project' : 'Import technical guideline templates to this project'} onClick={() => openGuidelineDrawer('project')}><Icon name="download" size={14} />Import tech project</button>}
            {listTab === 'guidelines' && <button className="btn sm ghost" disabled={availableWorkPackageGuidelines.length === 0} title={availableWorkPackageGuidelines.length === 0 ? 'All technical guideline templates are already linked to this work package' : 'Import technical guideline templates to this work package'} onClick={() => openGuidelineDrawer('workPackage')}><Icon name="download" size={14} />Import tech work package</button>}
            {listTab === 'guidelines' && <button className="btn sm ghost" disabled={availableProjectDesignTemplates.length === 0} title={availableProjectDesignTemplates.length === 0 ? 'All visual guideline templates are already linked to this project' : 'Import visual guideline template to this project'} onClick={() => openVisualGuidelineDrawer('project')}><Icon name="download" size={14} />Import visual project</button>}
            {listTab === 'guidelines' && <button className="btn sm ghost" disabled={availableWorkPackageDesignTemplates.length === 0} title={availableWorkPackageDesignTemplates.length === 0 ? 'All visual guideline templates are already linked to this work package' : 'Import visual guideline template to this work package'} onClick={() => openVisualGuidelineDrawer('workPackage')}><Icon name="download" size={14} />Import visual work package</button>}
            {listTab === 'excalidrawLibraries' && <button className="btn sm ghost" disabled={availableProjectLibraryTemplates.length === 0} title={availableProjectLibraryTemplates.length === 0 ? 'All Excalidraw library templates are already linked to this project' : 'Import Excalidraw library template to this project'} onClick={() => openLibraryTemplateDrawer('project')}><Icon name="download" size={14} />Import project</button>}
            {listTab === 'excalidrawLibraries' && <button className="btn sm ghost" disabled={availableWorkPackageLibraryTemplates.length === 0} title={availableWorkPackageLibraryTemplates.length === 0 ? 'All Excalidraw library templates are already linked to this work package' : 'Import Excalidraw library template to this work package'} onClick={() => openLibraryTemplateDrawer('workPackage')}><Icon name="download" size={14} />Import work package</button>}
            {listTab === 'milestones' && <button className="btn sm ghost" onClick={() => onDrawer('projectMilestone', null, { projectId: project.id, workPackageId: active.id })}><Icon name="plus" size={14} />Milestone</button>}
          </React.Fragment>}>
        {listTab === 'scope' ? (
          <React.Fragment>
            <div className="req-readiness">
              <div><strong>{reqPct}%</strong><span>accepted</span></div>
              <span className="req-readiness-bar"><i style={{ width: reqPct + '%' }} /></span>
              <span className="chip pos">{reqCounters.signedOff} accepted</span>
              <span className="chip">{reqCounters.draft} draft</span>
              <span className="chip warn">{reqCounters.openQuestion} open Q</span>
              {conceptTypeChips.map(([type, count]) => <span className="chip" key={type}>{type} {count}</span>)}
            </div>
            <div className="okf-browser">
              <aside className="okf-tree" aria-label="OKF scope concept tree">
                <div className="okf-tree-head">
                  <span><Icon name="bookOpen" size={16} /></span>
                  <div><strong>OKF bundle</strong><small>{filteredTitled.length} of {titled.length} node{titled.length === 1 ? '' : 's'}</small></div>
                </div>
                {filteredTitled.length ? (
                  <ScopeTreeNode node={scopeTree} selectedId={selectedScope && selectedScope.id} onSelect={setSelectedScopeId} />
                ) : (
                <div className="okf-tree-empty">{okfSearchQuery ? 'No OKF nodes match this search.' : 'No OKF nodes yet.'}</div>
                )}
              </aside>
              <section className="okf-preview">
                {selectedScope ? (
                  <React.Fragment>
                    <header className="okf-preview-head">
                      <div className="okf-preview-title">
                        <div className="okf-breadcrumbs">
                          {selectedScopeSegments.map((segment, index) => (
                            <React.Fragment key={`${segment}-${index}`}>
                              {index > 0 && <span>/</span>}
                              <span>{segment}</span>
                            </React.Fragment>
                          ))}
                        </div>
                        <h3>{selectedScope.title || 'Untitled scope concept'}</h3>
                        <div className="req-row-meta">
                          <span className="chip">{okfConceptType(selectedScope)}</span>
                          <Pill kind={selectedScope.statusLabel || selectedScope.status || 'Draft'} sm>{selectedScope.statusLabel || selectedScope.status || 'draft'}</Pill>
                          {selectedScope.tags && selectedScope.tags.slice(0, 4).map((tag) => <span className="chip" key={tag}>{tag}</span>)}
                        </div>
                      </div>
                      <div className="req-row-actions">
                        {selectedIsRequirement && !['accepted', 'implemented'].includes(window.AC.cleanProjectRequirementStatus(selectedScope.status)) && (
                          <button className="btn sm ghost" title="Accept scope concept" onClick={() => updateItem(selectedScope, { status: 'accepted' })}><Icon name="check" size={13} />Accept</button>
                        )}
                        <button className="icon-btn" title="Edit OKF node" onClick={() => openItem(selectedScope)}><Icon name="edit" size={14} /></button>
                        <button className="icon-btn" title="Delete OKF node" onClick={() => removeItem(selectedScope)}><Icon name="trash" size={14} /></button>
                      </div>
                    </header>
                    <div className="okf-preview-controls">
                      <RequirementSelectChip title="Work package" value={selectedScope.workPackageId} options={sectionOptions} onChange={(workPackageId) => updateItem(selectedScope, { workPackageId })} />
                      {selectedIsRequirement && <RequirementSelectChip title="Type" value={selectedScope.type} options={window.AC.PROJECT_REQUIREMENT_TYPES} clean={window.AC.cleanProjectRequirementType} onChange={(type) => updateItem(selectedScope, { type })} />}
                      <RequirementSelectChip title="Priority" value={selectedScope.priority} options={window.AC.PROJECT_REQUIREMENT_PRIORITIES} clean={window.AC.cleanProjectRequirementPriority} onChange={(priority) => updateItem(selectedScope, { priority })} />
                      <RequirementSelectChip title="Status" value={selectedScope.status} options={window.AC.PROJECT_REQUIREMENT_STATUSES} clean={window.AC.cleanProjectRequirementStatus} onChange={(status) => updateItem(selectedScope, { status })} />
                    </div>
                    {hasSelectedConnections ? (
                      <div className="okf-connections">
                        {selectedSources.length > 0 && (
                          <div className="okf-connection-group">
                            <strong>Citations</strong>
                            {selectedSources.map((source) => (
                              <div className="okf-connection" key={source.id || `${source.sourceType}:${source.sourceId}`}>
                                <span>{source.sourceType || 'source'}</span>
                                <b>{source.citation || source.sourceId}</b>
                              </div>
                            ))}
                          </div>
                        )}
                        {selectedArtifacts.length > 0 && (
                          <div className="okf-connection-group">
                            <strong>Artifacts</strong>
                            {selectedArtifacts.map((artifact) => (
                              <div className="okf-connection" key={artifact.id || `${artifact.artifactType}:${artifact.artifactId}:${artifact.relation}`}>
                                <span>{artifact.relation || artifact.artifactType || 'artifact'}</span>
                                <b>{artifact.artifactType}:{String(artifact.artifactId || '').slice(0, 8)}</b>
                              </div>
                            ))}
                          </div>
                        )}
                        {(selectedOutgoingLinks.length > 0 || selectedIncomingLinks.length > 0) && (
                          <div className="okf-connection-group">
                            <strong>Concept links</strong>
                            {selectedOutgoingLinks.map((link) => (
                              <button className="okf-connection" key={link.id || `out:${link.href}:${link.toConceptId}`} onClick={() => link.toConceptId && visibleConceptIds.has(link.toConceptId) && setSelectedScopeId(link.toConceptId)} disabled={!link.toConceptId || !visibleConceptIds.has(link.toConceptId)}>
                                <span>to</span>
                                <b>{link.href || okfConnectionLabel('to', link.toConceptId, conceptsById)}</b>
                              </button>
                            ))}
                            {selectedIncomingLinks.map((link) => (
                              <button className="okf-connection" key={link.id || `in:${link.href}:${link.fromConceptId}`} onClick={() => visibleConceptIds.has(link.fromConceptId) && setSelectedScopeId(link.fromConceptId)} disabled={!visibleConceptIds.has(link.fromConceptId)}>
                                <span>from</span>
                                <b>{okfConnectionLabel('from', link.fromConceptId, conceptsById)}</b>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null}
                    <div className="okf-md">
                      {selectedScopeMarkdown ? <MarkdownText text={selectedScopeMarkdown} /> : <p className="muted">No Markdown body yet.</p>}
                    </div>
                  </React.Fragment>
                ) : (
                  <div className="okf-preview-empty">
                    <Icon name="bookOpen" size={28} />
                    <span className="muted">{okfSearchQuery ? 'No OKF nodes match this search.' : 'No OKF nodes yet.'}</span>
                  </div>
                )}
              </section>
            </div>
          </React.Fragment>
        ) : listTab === 'techStack' ? (
          <div className="req-list">
            {techItems.length ? techItems.map((item) => (
              <div className="req-row" key={item.id}>
                <div className="req-row-main">
                  <div className="req-row-title">{item.tech}</div>
                  <div className="req-row-meta">
                    <span className="chip">{window.AC.cleanProjectTechStackCategory(item.category)}</span>
                  </div>
                </div>
                <div className="req-row-actions">
                  <button className="icon-btn" title="Edit tech stack item" onClick={() => openTechItem(item)}><Icon name="edit" size={14} /></button>
                  <button className="icon-btn" title="Delete tech stack item" onClick={() => removeTechItem(item)}><Icon name="trash" size={14} /></button>
                </div>
              </div>
            )) : (
              <div className="req-empty">
                <span className="muted">No tech stack items yet.</span>
              </div>
            )}
          </div>
        ) : listTab === 'guidelines' ? (
          <React.Fragment>
            <div className="req-list">
              {guidelineRows.length ? guidelineRows.map((template) => (
                <div className="req-row" key={template.key}>
                  <div className="req-row-main">
                    <div className="req-row-title">{template.title}</div>
                    <div className="req-row-meta">
                      <span className="chip">{template.kind}</span>
                      <span className="chip">{template.scope}</span>
                      <span className="chip">{template.source}</span>
                      <span className="chip">{(template.body || '').slice(0, 120)}{(template.body || '').length > 120 ? '...' : ''}</span>
                    </div>
                  </div>
                  <div className="req-row-actions">
                    {template.onEdit && <button className="icon-btn" title={`Edit ${template.kind.toLowerCase()} guideline`} onClick={template.onEdit}><Icon name="edit" size={14} /></button>}
                    <button className="icon-btn" title={`Remove ${template.kind.toLowerCase()} guideline`} onClick={template.onRemove}><Icon name="trash" size={14} /></button>
                  </div>
                </div>
              )) : (
                <div className="req-empty">
                  <span className="muted">No guidelines linked to this project or work package.</span>
                </div>
              )}
            </div>
            {localGuidelineDrawer && (
              <React.Fragment>
                <div className="overlay" onClick={closeLocalGuidelineDrawer} />
                <aside className="drawer">
                  <header>
                    <div style={{ flex: 1 }}>
                      <h2>{localGuidelineDrawer.guideline ? 'Edit guideline' : 'Create guideline'}</h2>
                      <div className="sub">{localGuidelineDrawer.kind === 'design' ? 'Visual' : 'Technical'} · {localGuidelineDrawer.scope === 'workPackage' ? active.name : 'Project'}</div>
                    </div>
                    <button className="icon-btn" onClick={closeLocalGuidelineDrawer}><Icon name="close" size={17} /></button>
                  </header>
                  <div className="body">
                    <div className="field"><label>Title</label><input className="inp" value={localGuidelineDrawer.title} onChange={(e) => setLocalGuidelineField('title', e.target.value)} placeholder="Guideline title" /></div>
                    <div className="field"><label>Guideline text</label><textarea className="inp" rows="12" value={localGuidelineDrawer.body} onChange={(e) => setLocalGuidelineField('body', e.target.value)} placeholder={localGuidelineDrawer.kind === 'design' ? 'Visual rules, UI constraints, interaction expectations...' : 'Architecture, integration, testing, repository constraints...'} /></div>
                  </div>
                  <footer>
                    <button className="btn ghost" onClick={closeLocalGuidelineDrawer}>Cancel</button>
                    <button className="btn" disabled={!String(localGuidelineDrawer.title || '').trim() || !String(localGuidelineDrawer.body || '').trim()} onClick={saveLocalGuideline}><Icon name="check" size={14} />Save</button>
                  </footer>
                </aside>
              </React.Fragment>
            )}
            {guidelineDrawerOpen && (
              <React.Fragment>
                <div className="overlay" onClick={closeGuidelineDrawer} />
                <aside className="drawer">
                  <header>
                    <div style={{ flex: 1 }}>
                      <h2>Import guidelines</h2>
                      <div className="sub">Add technical guideline templates to {guidelineScope === 'workPackage' ? active.name : 'this project'}</div>
                    </div>
                    <button className="icon-btn" onClick={closeGuidelineDrawer}><Icon name="close" size={17} /></button>
                  </header>
                  <div className="body">
                    <div className="field"><label>Guideline templates</label>
                      <div className="grid" style={{ gap: 8 }}>
                        {availableGuidelines.map((template) => {
                          const inputId = `import-guideline-${template.id}`;
                          return (
                            <div className="checkrow" key={template.id}>
                              <input id={inputId} type="checkbox" checked={selectedGuidelineTemplateIds.includes(template.id)} onChange={() => toggleGuidelineSelection(template.id)} />
                              <label htmlFor={inputId}>{template.title || 'Untitled guideline template'}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                      <button className="btn sm ghost" onClick={() => setSelectedGuidelineTemplateIds(availableGuidelines.map((template) => template.id))}>Select all</button>
                      <button className="btn sm ghost" onClick={() => setSelectedGuidelineTemplateIds([])}>Clear</button>
                    </div>
                    {!!selectedGuidelineTemplateIds.length && <div className="field"><label>Preview</label><textarea className="inp" rows="8" value={selectedGuidelineTemplateIds.map((id) => {
                      const template = availableGuidelines.find((item) => item.id === id);
                      return template ? `${template.title || 'Untitled guideline template'}\n\n${template.body || ''}` : '';
                    }).filter(Boolean).join('\n\n---\n\n')} readOnly disabled /></div>}
                  </div>
                  <footer>
                    <button className="btn ghost" onClick={closeGuidelineDrawer}>Cancel</button>
                    <button className="btn" disabled={selectedGuidelineTemplateIds.length === 0} onClick={importGuidelines}><Icon name="check" size={14} />Import</button>
                  </footer>
                </aside>
              </React.Fragment>
            )}
            {visualGuidelineDrawerOpen && (
              <React.Fragment>
                <div className="overlay" onClick={closeVisualGuidelineDrawer} />
                <aside className="drawer">
                  <header>
                    <div style={{ flex: 1 }}>
                      <h2>Link visual guideline</h2>
                      <div className="sub">{visualGuidelineScope === 'project' ? 'Project visual context' : `${active.name} · Work package visual context`}</div>
                    </div>
                    <button className="icon-btn" onClick={closeVisualGuidelineDrawer}><Icon name="close" size={17} /></button>
                  </header>
                  <div className="body">
                    <div className="field"><label>Visual guideline template</label><select className="inp" value={visualGuidelineTemplateId} onChange={(e) => setVisualGuidelineTemplateId(e.target.value)}>
                      {availableDesignTemplates.map((template) => <option key={template.id} value={template.id}>{template.title}</option>)}
                    </select></div>
                    {visualGuidelineTemplateId && <div className="field"><label>Preview</label><textarea className="inp" rows="8" value={(availableDesignTemplates.find((template) => template.id === visualGuidelineTemplateId) || {}).body || ''} readOnly disabled /></div>}
                  </div>
                  <footer>
                    <button className="btn ghost" onClick={closeVisualGuidelineDrawer}>Cancel</button>
                    <button className="btn" disabled={!visualGuidelineTemplateId} onClick={linkVisualGuideline}><Icon name="check" size={14} />Link</button>
                  </footer>
                </aside>
              </React.Fragment>
            )}
          </React.Fragment>
        ) : listTab === 'excalidrawLibraries' ? (
          <React.Fragment>
            <div className="req-list">
              {libraryRows.length ? libraryRows.map((template) => (
                <div className="req-row" key={template.key}>
                  <div className="req-row-main">
                    <div className="req-row-title">{template.title}</div>
                    <div className="req-row-meta">
                      <span className="chip">{template.scope}</span>
                      <span className="chip mono">{template.source}</span>
                      {!!template.description && <span className="chip">{template.description.slice(0, 120)}{template.description.length > 120 ? '...' : ''}</span>}
                    </div>
                  </div>
                  <div className="req-row-actions">
                    <button className="icon-btn" title="Remove Excalidraw library template" onClick={template.onRemove}><Icon name="trash" size={14} /></button>
                  </div>
                </div>
              )) : (
                <div className="req-empty">
                  <span className="muted">No Excalidraw libraries linked to this project or work package.</span>
                </div>
              )}
            </div>
            {libraryTemplateDrawerOpen && (
              <React.Fragment>
                <div className="overlay" onClick={closeLibraryTemplateDrawer} />
                <aside className="drawer">
                  <header>
                    <div style={{ flex: 1 }}>
                      <h2>Import Excalidraw library</h2>
                      <div className="sub">{libraryTemplateScope === 'project' ? 'Project sketch library context' : `${active.name} · Work package sketch library context`}</div>
                    </div>
                    <button className="icon-btn" onClick={closeLibraryTemplateDrawer}><Icon name="close" size={17} /></button>
                  </header>
                  <div className="body">
                    <div className="field"><label>Excalidraw library template</label><select className="inp" value={libraryTemplateId} onChange={(e) => setLibraryTemplateId(e.target.value)}>
                      {availableLibraryTemplates.map((template) => <option key={template.id} value={template.id}>{template.title}</option>)}
                    </select></div>
                    {libraryTemplateId && <div className="field"><label>Preview</label><textarea className="inp mono" rows="8" value={(() => {
                      const template = availableLibraryTemplates.find((item) => item.id === libraryTemplateId) || {};
                      return [template.source, template.description].filter(Boolean).join('\n\n');
                    })()} readOnly disabled /></div>}
                  </div>
                  <footer>
                    <button className="btn ghost" onClick={closeLibraryTemplateDrawer}>Cancel</button>
                    <button className="btn" disabled={!libraryTemplateId} onClick={linkLibraryTemplate}><Icon name="check" size={14} />Import</button>
                  </footer>
                </aside>
              </React.Fragment>
            )}
          </React.Fragment>
        ) : listTab === 'milestones' ? (
          <React.Fragment>
            <div className="req-readiness">
              <span className="chip pos">{doneMilestones}/{milestones.length} done</span>
              <span className="chip">{linkedTickets.done}/{linkedTickets.total} linked tickets</span>
              <span className="chip">{active.name}</span>
            </div>
            <div style={{ display: 'grid', gap: 8 }}>
              {milestones.length ? milestones.map((m, i) => (
                <MilestoneRow key={m.id} project={project} milestone={m} ticketStats={ticketStatsByMilestone[m.id] || { total: 0, done: 0, blocked: 0 }} index={i} count={milestones.length} onAction={onAction} onDrawer={onDrawer} />
              )) : (
                <div className="req-empty">
                  <span className="muted">No milestones in this work package yet.</span>
                </div>
              )}
            </div>
          </React.Fragment>
        ) : listTab === 'screenDrafts' ? (
          ScreenDraftsPanel
            ? <ScreenDraftsPanel project={project} active={active} screenDrafts={screenDrafts} onAction={handleScreenDraftAction} onZenChange={setScreenDraftZen} onActiveDraftChange={onVoiceContextChange} />
            : <div className="req-empty"><span className="muted">Screen Drafts panel is not loaded.</span></div>
        ) : listTab === 'sketches' ? (
          <window.ProjectSketchPanel project={project} active={active} sketches={sketches} onAction={onAction} onZenChange={setSketchZen} />
        ) : (
          null
        )}
        </Panel>
      </div>
    );
  }

  function RequirementSelectChip({ title, value, options, clean, onChange }) {
    const normalizedOptions = (options || []).map((option) => typeof option === 'string' ? { value: option, label: option } : option);
    const cleanedValue = clean ? clean(value) : value;
    const current = normalizedOptions.find((option) => option.value === cleanedValue) || normalizedOptions.find((option) => String(option.value).toLowerCase() === String(cleanedValue || '').toLowerCase());
    const selected = current ? current.value : cleanedValue;
    const selectOptions = selected && !normalizedOptions.some((option) => option.value === selected)
      ? [{ value: selected, label: selected }, ...normalizedOptions]
      : normalizedOptions;
    return (
      <select className="chip req-chip-select" title={title} value={selected || ''} onChange={(e) => onChange(e.target.value)}>
        {selectOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    );
  }

  /* ---------- milestone row (unchanged contract: onAction / onDrawer) ---------- */
  function MilestoneRow({ project, milestone, ticketStats, index, count, onAction, onDrawer, showDocuments = true }) {
    const m = milestone;
    const [docOver, setDocOver] = useState(false);
    const docTarget = { milestoneId: m.id };
    const docs = window.projectDocumentsFor ? window.projectDocumentsFor(project, docTarget) : [];
    const dropProps = window.projectDocumentDropProps ? window.projectDocumentDropProps(project, onAction, docTarget, setDocOver) : {};
    const invAmt = milestoneInvoiceAmount(m);
    const overdue = !m.completed && m.dueDate && String(m.dueDate) < today;
    const total = ticketStats.total, done = ticketStats.done;
    const pct = total ? done / total : 0;
    const canFinish = m.completed || total === 0 || done === total;
    const status = m.completed ? 'Completed'
      : ticketStats.blocked > 0 ? 'Blocked'
        : total > 0 && done === total ? 'Ready to finish'
          : done > 0 ? 'Partly completed'
            : overdue ? 'Overdue' : 'Open';
    const hasEffort = m.estimatedHours > 0 || m.actualHours > 0 || m.hourlyRate > 0 || m.actualCost > 0;
    const hoursPct = m.estimatedHours > 0 ? Math.min(1, (m.actualHours || 0) / m.estimatedHours) : 0;
    return (
      <div className={'ms-row' + (m.completed ? ' done' : '') + (docOver ? ' doc-over' : '')} {...dropProps}>
        <div className="ms-row-top">
          <input className="pd-check ms-row-check" type="checkbox" title={canFinish ? 'Mark complete' : 'Finish assigned tickets first'}
            disabled={!canFinish} checked={!!m.completed} onChange={() => onAction('toggleProjectMilestone', { project, milestoneId: m.id })} />
          <span style={{ flex: 1, minWidth: 0, fontWeight: 600, textDecoration: m.completed ? 'line-through' : 'none', color: m.completed ? 'var(--muted)' : 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</span>
          <Pill kind={status} sm />
          {m.dueDate && <span className={'chip' + (overdue ? ' warn' : '')}><Icon name="clock" size={11} />{fmtDate(m.dueDate)}</span>}
          <span className="ms-row-tools">
            <button className="icon-btn" title="Move up" disabled={index === 0} onClick={() => onAction('reorderProjectMilestone', { project, milestoneId: m.id, direction: -1 })}><Icon name="arrowUp" size={14} /></button>
            <button className="icon-btn" title="Move down" disabled={index === count - 1} onClick={() => onAction('reorderProjectMilestone', { project, milestoneId: m.id, direction: 1 })}><Icon name="arrowDown" size={14} /></button>
            <button className="icon-btn" title="Edit milestone" onClick={() => onDrawer('projectMilestone', m.id, { projectId: project.id })}><Icon name="edit" size={14} /></button>
            <button className="icon-btn" title="Delete milestone" onClick={() => onAction('deleteProjectMilestone', { ...m, projectId: project.id })}><Icon name="trash" size={14} /></button>
          </span>
        </div>
        <div className="ms-row-tickets">
          {total > 0 ? (
            <React.Fragment>
              <span className="progressbar" style={{ width: 86 }}><span style={{ width: (pct * 100) + '%', background: done === total ? 'var(--pos)' : 'var(--primary)' }} /></span>
              <span className="chip">{done}/{total} tickets</span>
              <span className={'chip ' + (canFinish ? 'pos' : 'warn')}>{canFinish ? 'Can finish' : 'Needs ticket work'}</span>
            </React.Fragment>
          ) : <span className="muted" style={{ fontSize: 12 }}>No assigned tickets.</span>}
        </div>
        <div className="ms-row-effort">
          {hasEffort ? (
            <React.Fragment>
              {m.estimatedHours > 0 && <span className="chip">Est. {decimal(m.estimatedHours)}h</span>}
              {m.actualHours > 0 && <span className="chip">Actual {decimal(m.actualHours)}h</span>}
              {m.hourlyRate > 0 && <span className="chip">{money(m.hourlyRate)}/h</span>}
              {invAmt > 0 && <span className="chip">{money(invAmt)}</span>}
              {m.estimatedHours > 0 && <span className="ms-hours" title={`${decimal(m.actualHours || 0)}h of ${decimal(m.estimatedHours)}h`}><span style={{ width: (hoursPct * 100) + '%', background: hoursPct >= 1 ? 'var(--warn)' : 'var(--primary)' }} /></span>}
            </React.Fragment>
          ) : <span className="muted" style={{ fontSize: 12 }}>No effort logged.</span>}
          {showDocuments && docs.map((doc) => (
            <button key={doc.id} type="button" className="chip pd-docchip" title={window.projectDocumentTitle ? window.projectDocumentTitle(doc) : doc.fileName} onClick={() => onAction('openProjectDocument', { projectId: project.id, documentId: doc.id })}>
              <Icon name="doc" size={11} />{doc.fileName}
            </button>
          ))}
        </div>
      </div>
    );
  }

  function WorkPackageScopeBar({ planning, project, onAction, Inline }) {
    const { draft, active, setActiveId } = planning;
    if (!window.AC.projectHasSoftwareFields(project)) return null;
    const activeWorkPackage = (project.workPackages || []).find((workPackage) => workPackage.id === active.id) || null;
    const itemLabel = window.AC.projectItemLabel(project);
    return (
      <div className="pl-sectionbar">
        <span className="pl-seclabel">{itemLabel}</span>
        <div className="segmented planning-sections" style={{ flexWrap: 'wrap' }}>
          {draft.map((s) => <button key={s.id} className={s.id === active.id ? 'on' : ''} onClick={() => setActiveId(s.id)}>{s.name}</button>)}
        </div>
        <button className="btn sm ghost" onClick={planning.addWorkPackage}><Icon name="plus" size={14} />{itemLabel}</button>
        <span style={{ flex: 1 }} />
        <label className="kb-toggle" title="Ask the senior developer to delegate integration test authoring when useful for implemented tickets">
          <input type="checkbox" checked={!!active.createTests} onChange={(event) => planning.patchSection(active.id, { createTests: event.target.checked })} />
          Create tests
        </label>
        <span className="pl-rename"><Icon name="edit" size={12} className="faint" /><Inline value={active.name} onCommit={(v) => planning.patchSection(active.id, { name: v })} style={{ fontWeight: 600 }} /></span>
        {activeWorkPackage && <React.Fragment><span className="pl-seclabel">Phase</span><window.WorkPackagePhaseStepper project={project} workPackage={activeWorkPackage} onAction={onAction} compact /></React.Fragment>}
      </div>
    );
  }

  function SectionProgressPanel({ planning, project, onDrawer, onAction }) {
    const active = planning.active;
    const activeWorkPackage = (project.workPackages || []).find((workPackage) => workPackage.id === active.id) || null;
    const sectionMs = (project.milestones || []).filter((m) => m.workPackageId === active.id || (!m.workPackageId && active.position === 0));
    const doneMs = sectionMs.filter((m) => m.completed).length;
    const estH = sectionMs.reduce((s, m) => s + (Number(m.estimatedHours) || 0), 0);
    const actH = sectionMs.reduce((s, m) => s + (Number(m.actualHours) || 0), 0);
    const cats = planning.cats;
    const ticketStatsByMilestone = cats.reduce((stats, cat) => {
      cat.items.forEach((item) => {
        if (!item.milestoneId) return;
        const cur = stats[item.milestoneId] || { total: 0, done: 0, blocked: 0 };
        cur.total += 1;
        if (item.done) cur.done += 1;
        if (item.status === 'Blocked' || item.status === 'Missing info') cur.blocked += 1;
        stats[item.milestoneId] = cur;
      });
      return stats;
    }, {});
    const linked = sectionMs.reduce((a, m) => { const s = ticketStatsByMilestone[m.id] || { total: 0, done: 0, blocked: 0 }; a.total += s.total; a.done += s.done; a.blocked += s.blocked; return a; }, { total: 0, done: 0, blocked: 0 });
    const msPct = sectionMs.length ? doneMs / sectionMs.length : 0;
    const docCount = (project.documents || []).filter((doc) => doc.workPackageId === active.id || (!doc.workPackageId && active.position === 0)).length;
    return (
      <React.Fragment>
        <Panel title="Section progress" sub={`${active.name} · ${doneMs}/${sectionMs.length} milestones`}>
          <div className="ring-wrap">
            <Ring value={msPct} size={104} />
            <div className="pd-tiles" style={{ flex: 1, gridTemplateColumns: 'repeat(4, 1fr)' }}>
              <div className="pd-tile"><div className="k">Milestones</div><div className="v">{doneMs}/{sectionMs.length}</div></div>
              <div className="pd-tile"><div className="k">Effort</div><div className="v">{decimal(actH)}<span className="faint" style={{ fontSize: 11 }}> / {decimal(estH)}h</span></div></div>
              <div className="pd-tile"><div className="k">Documents</div><div className="v">{docCount}</div></div>
              <div className="pd-tile"><div className="k">Phase</div><div className="v sm">{activeWorkPackage ? activeWorkPackage.phase || window.AC.projectPhases(project)[0] : '—'}</div></div>
            </div>
          </div>
        </Panel>
        <Panel title="Milestones" sub={`${active.name} · ${linked.done}/${linked.total} linked tickets`} actions={<button className="btn sm ghost" onClick={() => onDrawer('projectMilestone', null, { projectId: project.id, workPackageId: active.id })}><Icon name="plus" size={14} />Milestone</button>}>
          <div style={{ display: 'grid', gap: 8 }}>
            {sectionMs.length ? sectionMs.map((m, i) => (
              <MilestoneRow key={m.id} project={project} milestone={m} ticketStats={ticketStatsByMilestone[m.id] || { total: 0, done: 0, blocked: 0 }} index={i} count={sectionMs.length} onAction={onAction} onDrawer={onDrawer} />
            )) : <span className="muted" style={{ fontSize: 12 }}>No milestones in this work package yet.</span>}
          </div>
        </Panel>
      </React.Fragment>
    );
  }

  const CONTEXT_TYPES = [
    ['all', 'All', 'inbox'],
    ['note', 'Notes', 'note'],
    ['email', 'Email', 'mail'],
    ['file', 'Files', 'doc'],
  ];
  const CONTEXT_TYPE_META = {
    note: { label: 'Note', icon: 'note', color: 'var(--primary)' },
    email: { label: 'Email', icon: 'mail', color: 'var(--primary-strong)' },
    file: { label: 'File', icon: 'doc', color: 'var(--ink-2)' },
  };

  function contextMilestoneIds(item) {
    if (Array.isArray(item.milestoneIds)) return item.milestoneIds.filter(Boolean);
    return item.milestoneId ? [item.milestoneId] : [];
  }

  function buildProjectContextItems(project, db, recentDocuments = []) {
    if (!project) return [];
    const projectScoped = !window.AC.projectHasSoftwareFields(project);
    const projectDocumentIds = new Set((project.documents || []).map((doc) => doc.id));
    const documents = [
      ...(project.documents || []),
      ...(recentDocuments || []).filter((doc) => doc && doc.id && !projectDocumentIds.has(doc.id)),
    ];
    const workPackagesById = new Map((project.workPackages || []).map((workPackage) => [workPackage.id, workPackage]));
    const milestones = (project.milestones || []).map((m) => ({ ...m, workPackageName: (workPackagesById.get(m.workPackageId) || {}).name || 'Project' }));
    const milestonesById = new Map(milestones.map((m) => [m.id, m]));
    const customersById = new Map(((db && db.customers) || []).map((customer) => [customer.id, customer]));
    return [
      ...documents.map((doc) => {
        const m = doc.milestoneId ? milestonesById.get(doc.milestoneId) : null;
        const workPackage = doc.workPackageId ? workPackagesById.get(doc.workPackageId) : m ? workPackagesById.get(m.workPackageId) : null;
        const milestoneIds = contextMilestoneIds(doc);
        return {
          key: `file:${doc.id}`, id: doc.id, sourceId: doc.id, type: 'file', isContextItem: true, raw: doc, title: doc.fileName || 'File',
          body: doc.description || doc.contentType || 'Project file', meta: `File · ${fmtDateTime(doc.uploadedAt)}`,
          detail: doc.description || doc.contentType || 'Project file', sortDate: doc.uploadedAt, projectId: project.id, projectName: project.name,
          workPackageName: projectScoped ? 'Project' : workPackage ? workPackage.name : 'Project base', milestoneIds, milestoneNames: milestoneIds.map((id) => (milestonesById.get(id) || {}).title).filter(Boolean).join(', '), isRequirement: !!doc.isRequirement,
          deleteAction: 'deleteProjectDocument', deletePayload: { projectId: project.id, documentId: doc.id, fileName: doc.fileName },
        };
      }),
      ...(project.emails || []).map((email) => {
        const milestoneIds = contextMilestoneIds(email);
        return {
          key: `email:${email.id || email.messageId || email.uid}`, id: email.id, sourceId: email.id, type: 'email', isContextItem: true, raw: email,
          title: email.subject || '(no subject)', body: email.preview || email.snippet || '', detail: email.preview || email.snippet || '',
          meta: `${email.from || 'Unknown sender'} · ${fmtDateTime(email.messageDate || email.date || email.attachedAt)}`,
          sortDate: email.messageDate || email.date || email.attachedAt, projectId: project.id, projectName: project.name,
          workPackageName: projectScoped ? 'Project' : email.workPackageId && workPackagesById.get(email.workPackageId) ? workPackagesById.get(email.workPackageId).name : 'All work packages', milestoneIds, milestoneNames: milestoneIds.map((id) => (milestonesById.get(id) || {}).title).filter(Boolean).join(', '),
          status: email.status === 'processed' ? 'Processed' : 'Unprocessed', isRequirement: !!email.isRequirement,
          deleteAction: 'deleteProjectEmail', deletePayload: { projectId: project.id, emailId: email.id, subject: email.subject },
        };
      }),
      ...(((db && db.notes) || [])).filter((item) => item.projectId === project.id).map((note) => {
        const customer = note.customerId ? customersById.get(note.customerId) : null;
        const workPackage = note.workPackageId ? workPackagesById.get(note.workPackageId) : null;
        const milestoneIds = contextMilestoneIds(note);
        return {
          key: `note:${note.id}`, id: note.id, sourceId: note.id, type: 'note', isContextItem: true, raw: note,
          title: note.subject || 'Note', body: note.body || '', detail: note.body || '',
          meta: `${customer ? customer.name : 'Project'} · ${fmtDateTime(note.occurredAt || note.occurredOn || note.date)}`,
          sortDate: note.occurredAt || note.occurredOn || note.date, projectId: project.id, projectName: project.name, customerId: note.customerId, customerName: customer && customer.name,
          workPackageName: projectScoped ? 'Project' : workPackage ? workPackage.name : 'All work packages', milestoneIds, milestoneNames: milestoneIds.map((id) => (milestonesById.get(id) || {}).title).filter(Boolean).join(', '), isRequirement: !!note.isRequirement,
          deleteAction: 'deleteProjectNote', deletePayload: { id: note.id, subject: note.subject },
        };
      }),
    ].sort((a, b) => String(b.sortDate || '').localeCompare(String(a.sortDate || '')));
  }

  function projectContextItemAiReference(project, item) {
    const raw = item.raw || {};
    const typeMeta = {
      file: { label: 'Project document', retrievalType: 'file' },
      email: { label: 'Project email', retrievalType: 'email' },
      note: { label: 'Project note', retrievalType: 'note' },
    };
    const meta = typeMeta[item.type] || { label: 'Project context', retrievalType: item.type || 'context' };
    const itemId = String(item.id || item.sourceId || '').trim();
    const sourceEmailId = item.type === 'email' ? String(raw.sourceEmailId || raw.SourceEmailId || '').trim() : '';
    const retrievalTool = item.type === 'file' ? 'document_get'
      : item.type === 'email' ? 'email_message_get'
        : item.type === 'note' ? 'project_note_get'
          : '';
    const retrievalArgs = item.type === 'file'
      ? { documentId: itemId, projectId: project.id || '' }
      : item.type === 'email'
        ? { emailId: sourceEmailId }
        : item.type === 'note'
          ? { projectId: project.id || '', noteId: itemId }
          : {};
    return {
      type: meta.label,
      id: itemId,
      label: item.title || 'Untitled context item',
      url: `${window.location.origin}${window.location.pathname}#/projects/${encodeURIComponent(project.id)}/context`,
      fields: {
        Project: project.name || '',
        ProjectId: project.id || '',
        ContextType: meta.retrievalType,
        ContextItemId: itemId,
        RetrievalTool: retrievalTool,
        RetrievalArgs: JSON.stringify(retrievalArgs),
        DocumentId: item.type === 'file' ? item.id : '',
        ProjectEmailId: item.type === 'email' ? item.id : '',
        SourceEmailId: sourceEmailId,
        NoteId: item.type === 'note' ? item.id : '',
        WorkPackage: item.workPackageName || '',
        Milestones: item.milestoneNames || '',
        Description: item.body || item.detail || '',
        ContentType: raw.contentType || '',
        Sender: item.type === 'email' ? raw.from || '' : '',
        Status: item.status || raw.status || '',
        Source: item.type === 'file' ? raw.externalProvider || 'Local file' : '',
        ExternalPath: item.type === 'file' ? raw.externalPath || '' : '',
        ExternalUrl: item.type === 'file' ? raw.externalUrl || '' : '',
        OccurredAt: item.type === 'note' ? raw.occurredAt || raw.occurredOn || item.sortDate || '' : '',
        MessageDate: item.type === 'email' ? raw.messageDate || raw.date || raw.attachedAt || item.sortDate || '' : '',
        UploadedAt: item.type === 'file' ? raw.uploadedAt || item.sortDate || '' : '',
        ScopeSource: item.isRequirement ? 'Yes' : '',
      },
    };
  }

  function isContextFileDrag(e) {
    return Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes('Files');
  }

  function ContextUploadProgress({ progress }) {
    if (!progress) return null;
    return (
      <div className="ctx-upload-progress" role="status" aria-live="polite">
        <div className="ctx-upload-progress-head">
          <span>Uploading {progress.current}/{progress.total}: {progress.fileName}</span>
          <strong>{progress.percent}%</strong>
        </div>
        <div className="ctx-upload-progress-bar"><span style={{ width: progress.percent + '%' }} /></div>
      </div>
    );
  }

  const CAPTURE_TYPE_META = {
    note: CONTEXT_TYPE_META.note,
    file: CONTEXT_TYPE_META.file,
    package: { label: 'Package', icon: 'box' },
  };

  function ProjectContextCapture({ project, db, active, milestones, uploadProgress, onUploadFiles, onUploadPackage, onAction, defaultOpen }) {
    const projectScoped = !window.AC.projectHasSoftwareFields(project);
    const [type, setType] = useState('note');
    const [title, setTitle] = useState('');
    const [body, setBody] = useState('');
    const [milestoneId, setMilestoneId] = useState('');
    const [over, setOver] = useState(false);
    const [open, setOpen] = useState(() => !!defaultOpen);
    const captureUploadProgress = uploadProgress && uploadProgress.dropTarget === 'capture' ? uploadProgress : null;
    const uploadTarget = () => ({ ...(milestoneId ? { milestoneId } : {}), dropTarget: 'capture' });
    const uploadFiles = (files) => onUploadFiles(files, uploadTarget());
    const uploadPackage = (files) => onUploadPackage(files, uploadTarget());
    const captureDrop = (e) => {
      if (!isContextFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setOver(false);
      setType('file');
      uploadFiles(e.dataTransfer.files);
    };
    const captureDragOver = (e) => {
      if (!isContextFileDrag(e)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'copy';
      setOver(true);
    };
    const submit = async (e) => {
      e.preventDefault();
      const subject = title.trim();
      const text = body.trim();
      if (!subject && !text) return;
      await onAction('createProjectNote', {
        projectId: project.id,
        note: {
          projectId: project.id,
          workPackageId: projectScoped ? null : active.id,
          customerId: null,
          contactPersonId: null,
          milestoneIds: milestoneId ? [milestoneId] : [],
          subject: subject || 'Note',
          body: text,
          type,
        },
      });
      setTitle('');
      setBody('');
      setMilestoneId('');
    };
    return (
      <details className={'panel ctx-capture' + (over ? ' over' : '')} open={open} onToggle={(e) => setOpen(e.currentTarget.open)}
        onDragEnter={captureDragOver}
        onDragOver={captureDragOver}
        onDragLeave={(e) => { if (!isContextFileDrag(e)) return; if (!e.currentTarget.contains(e.relatedTarget)) setOver(false); }}
        onDrop={captureDrop}>
        <summary className="ctx-capture-head"
          onDragOver={captureDragOver}
          onDragLeave={(e) => { if (!isContextFileDrag(e)) return; setOver(false); }}
          onDrop={captureDrop}>
          <h2><Icon name="inbox" size={15} />Capture context</h2>
          <span className="ctx-capture-hint">{type === 'package' ? 'Folders and zips become agent-readable packages.' : type === 'file' ? 'Files are added to the project root unless routed.' : 'Emails originate from the Email tab.'}</span>
          {captureUploadProgress && <ContextUploadProgress progress={captureUploadProgress} />}
        </summary>
        <div className="segmented ctx-capture-types">
          {['note', 'file', 'package'].map((item) => <button type="button" key={item} className={type === item ? 'on' : ''} onClick={() => setType(item)}><Icon name={CAPTURE_TYPE_META[item].icon} size={13} />{CAPTURE_TYPE_META[item].label}</button>)}
        </div>
        <form className="ctx-capture-body" onSubmit={submit}>
          <div className="ctx-capture-main">
            {type === 'package' ? (
              <div className={'ctx-dropzone ctx-package-zone' + (captureUploadProgress ? ' busy' : '')}>
                <Icon name="box" size={22} />
                <strong>Add a design handoff folder or zip</strong>
                <div className="ctx-package-actions">
                  <label className="btn sm ghost">
                    Choose folder
                    <input type="file" multiple webkitdirectory="" directory="" disabled={!!captureUploadProgress} style={{ display: 'none' }} onChange={(e) => { uploadPackage(e.target.files); e.target.value = ''; }} />
                  </label>
                  <label className="btn sm ghost">
                    Choose zip
                    <input type="file" accept=".zip,application/zip,application/x-zip-compressed" disabled={!!captureUploadProgress} style={{ display: 'none' }} onChange={(e) => { uploadPackage(e.target.files); e.target.value = ''; }} />
                  </label>
                </div>
              </div>
            ) : type === 'file' ? (
              <label className={'ctx-dropzone' + (over ? ' over' : '') + (captureUploadProgress ? ' busy' : '')}
                onDragOver={captureDragOver}
                onDragLeave={(e) => { if (!isContextFileDrag(e)) return; setOver(false); }}
                onDrop={captureDrop}>
                <Icon name="paperclip" size={22} />
                <strong>Drag files here to add them to the project</strong>
                <span className="btn sm ghost">Choose files</span>
                <input type="file" multiple disabled={!!captureUploadProgress} style={{ display: 'none' }} onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} />
              </label>
            ) : (
              <React.Fragment>
                <input className="inp" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Note title" />
                <textarea className="inp" rows="2" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Details" />
              </React.Fragment>
            )}
          </div>
          <div className="ctx-capture-side">
            <label>
              <span className="pl-seclabel">Route to milestone</span>
              <select className="inp" value={milestoneId} disabled={!!captureUploadProgress} onChange={(e) => setMilestoneId(e.target.value)}>
                <option value="">Leave unrouted (triage)</option>
                {milestones.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
              </select>
            </label>
            {type === 'note' ? <button className="btn" type="submit"><Icon name="plus" size={14} />Save note</button> : <span className="ctx-file-hint">Selected route is sent as the upload target.</span>}
          </div>
        </form>
      </details>
    );
  }

  function activeActionCenterRunsForContextItem(actionCenterOverview, item) {
    const scopeTypeByItemType = {
      file: 'project-document',
      email: 'project-email',
      note: 'project-note',
    };
    const scopeType = scopeTypeByItemType[item.type] || String(item.type || '').toLowerCase();
    const scopeId = String(item.id || '');
    return ((actionCenterOverview && actionCenterOverview.runs) || []).filter((run) => {
      const status = String(run.status || '').trim().toLowerCase();
      if (status === 'done' || status === 'error') return false;
      return String(run.contextScopeType || '').toLowerCase() === scopeType
        && String(run.contextScopeId || '') === scopeId;
    });
  }

  function ProjectContextInbox({ project, db, items, milestones, filter, setFilter, search, setSearch, needsRouting, setNeedsRouting, actionCenterOverview, bridgeActiveEmployeeIds, bridgeActivityByEmployeeId, bridgeActivityByRunId, onOpenItem, onDrawer, onAction, onSelectTab, onToast, onError, projectScoped = false }) {
    const counts = CONTEXT_TYPES.reduce((acc, [key]) => {
      acc[key] = key === 'all' ? items.length : items.filter((item) => item.type === key).length;
      return acc;
    }, {});
    const unroutedCount = items.filter((item) => contextMilestoneIds(item).length === 0).length;
    const q = search.trim().toLowerCase();
    const filtered = items.filter((item) => {
      if (filter !== 'all' && item.type !== filter) return false;
      if (needsRouting && contextMilestoneIds(item).length > 0) return false;
      if (!q) return true;
      return [item.title, item.body, item.meta, item.workPackageName].some((value) => String(value || '').toLowerCase().includes(q));
    });
    const updateRoutes = (item, milestoneIds) => onAction('updateProjectContextMilestones', { projectId: project.id, type: item.type, itemId: item.id, milestoneIds });
    const updateRequirement = (item) => {
      const next = !item.isRequirement;
      if (item.type === 'file') return onAction('updateProjectDocumentRequirement', { projectId: project.id, documentId: item.id, isRequirement: next });
      if (item.type === 'email') return onAction('updateProjectEmailStatus', { projectId: project.id, emailId: item.id, status: item.raw.status || 'unprocessed', isRequirement: next });
      return onAction('updateProjectNote', { projectId: project.id, noteId: item.id, note: { ...item.raw, isRequirement: next } });
    };
    const SharedContextItemCard = window.ContextItemCard;
    const actionCenterAgents = (actionCenterOverview && actionCenterOverview.agents) || [];
    return (
      <AiEmployeeContextActions db={db} onToast={onToast} onError={onError}>
        {({ contextMenuProps }) => (
      <section className="panel ctx-inbox">
        <div className="ctx-panel-head">
          <h2>Context inbox</h2>
          <span className="chip primary-soft">{projectScoped ? 'Project' : 'All work packages'}</span>
          <span className="muted">{items.length} items · {unroutedCount} need routing</span>
          <span className="spacer" />
          <label className="search ctx-search"><Icon name="search" size={14} /><input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search context..." /></label>
        </div>
        <div className="ctx-filter-row">
          {CONTEXT_TYPES.map(([key, label, icon]) => <button key={key} type="button" className={'chip ctx-filter' + (filter === key ? ' on' : '')} onClick={() => setFilter(key)}><Icon name={icon} size={12} />{label}<b>{counts[key]}</b></button>)}
          <span className="spacer" />
          <button type="button" className={'chip ctx-filter warn' + (needsRouting ? ' on' : '')} onClick={() => setNeedsRouting(!needsRouting)}><Icon name="alert" size={12} />Needs routing<b>{unroutedCount}</b></button>
        </div>
        <div className="ctx-inbox-list">
          {filtered.length && SharedContextItemCard ? filtered.map((item) => (
            <SharedContextItemCard
              key={item.key}
              item={item}
              meta={CONTEXT_TYPE_META[item.type] || CONTEXT_TYPE_META.note}
              routeIds={contextMilestoneIds(item)}
              routeOptions={milestones}
              onOpen={onOpenItem}
              onUpdateRoutes={updateRoutes}
              onUpdateRequirement={updateRequirement}
              onOpenSource={(sourceItem) => sourceItem.type === 'file' ? onAction('openProjectDocument', { projectId: project.id, documentId: sourceItem.id }) : sourceItem.type === 'email' ? onSelectTab && onSelectTab('email') : onDrawer('note', sourceItem.id)}
              onEdit={(sourceItem) => onDrawer('note', sourceItem.id)}
              onRemove={(sourceItem) => sourceItem.deleteAction && onAction(sourceItem.deleteAction, sourceItem.deletePayload)}
              contextMenuProps={(sourceItem) => contextMenuProps(projectContextItemAiReference(project, sourceItem))}
              scopeActivity={<ActionCenterScopeActivity runs={activeActionCenterRunsForContextItem(actionCenterOverview, item)} agents={actionCenterAgents} bridgeActiveEmployeeIds={bridgeActiveEmployeeIds} bridgeActivityByEmployeeId={bridgeActivityByEmployeeId} bridgeActivityByRunId={bridgeActivityByRunId} />}
            />
          )) : <div className="ctx-empty">No context matches this filter.</div>}
        </div>
      </section>
        )}
      </AiEmployeeContextActions>
    );
  }

  function ProjectContextTab({ planning, project, db, actionCenterOverview, bridgeActiveEmployeeIds, bridgeActivityByEmployeeId, bridgeActivityByRunId, onDrawer, onAction, onSelectTab, onToast, onError, Inline }) {
    const { active } = planning;
    const [filter, setFilter] = useState('all');
    const [needsRouting, setNeedsRouting] = useState(false);
    const [search, setSearch] = useState('');
    const [selectedContextItem, setSelectedContextItem] = useState(null);
    const [recentDocuments, setRecentDocuments] = useState([]);
    const [uploadProgress, setUploadProgress] = useState(null);
    React.useEffect(() => { setRecentDocuments([]); }, [project.id]);
    const rememberDocument = (doc, target = {}) => {
      if (!doc || !doc.id) return;
      const routed = target.milestoneId && !doc.milestoneId && !Array.isArray(doc.milestoneIds)
        ? { ...doc, milestoneId: target.milestoneId }
        : doc;
      setRecentDocuments((current) => current.some((item) => item.id === routed.id) ? current : [routed, ...current]);
    };
    const uploadFiles = async (files, target = {}) => {
      if (uploadProgress) return;
      const fileList = Array.from(files || []);
      if (!fileList.length) return;
      const totals = new Map(fileList.map((file) => [file, file.size || 0]));
      const loaded = new Map(fileList.map((file) => [file, 0]));
      const totalBytes = fileList.reduce((sum, file) => sum + (file.size || 0), 0);
      const updateUploadProgress = (index, file, uploaded) => {
        loaded.set(file, Math.min(uploaded || 0, totals.get(file) || 0));
        const loadedBytes = Array.from(loaded.values()).reduce((sum, value) => sum + value, 0);
        const percent = totalBytes ? Math.min(99, Math.round((loadedBytes / totalBytes) * 100)) : 0;
        setUploadProgress({ fileName: file.name, current: index + 1, total: fileList.length, percent, dropTarget: target.dropTarget || 'capture', milestoneId: target.milestoneId || null });
      };
      setUploadProgress({ fileName: fileList[0].name, current: 1, total: fileList.length, percent: 0, dropTarget: target.dropTarget || 'capture', milestoneId: target.milestoneId || null });
      try {
        for (let index = 0; index < fileList.length; index += 1) {
          const file = fileList[index];
          const uploaded = await onAction('uploadProjectDocument', {
            projectId: project.id,
            file,
            ...target,
            onProgress: (progress) => updateUploadProgress(index, file, progress.loaded),
          });
          if (uploaded === false) break;
          rememberDocument(uploaded, target);
          loaded.set(file, totals.get(file) || 0);
          setUploadProgress({ fileName: file.name, current: index + 1, total: fileList.length, percent: index === fileList.length - 1 ? 100 : Math.round(((index + 1) / fileList.length) * 100), dropTarget: target.dropTarget || 'capture', milestoneId: target.milestoneId || null });
        }
      } finally {
        window.setTimeout(() => setUploadProgress(null), 450);
      }
    };
    const uploadPackage = async (files, target = {}) => {
      if (uploadProgress) return;
      const fileList = Array.from(files || []);
      if (!fileList.length) return;
      const totalBytes = fileList.reduce((sum, file) => sum + (file.size || 0), 0);
      const displayName = fileList.length === 1 ? fileList[0].name : (fileList[0].webkitRelativePath || fileList[0].name || 'Package').split('/')[0];
      setUploadProgress({ fileName: displayName, current: 1, total: 1, percent: 0, dropTarget: target.dropTarget || 'capture', milestoneId: target.milestoneId || null });
      try {
        const uploaded = await onAction('uploadProjectDocumentPackage', {
          projectId: project.id,
          files: fileList,
          name: displayName,
          ...target,
          onProgress: (progress) => {
            const loaded = progress.loaded || 0;
            const total = progress.total || totalBytes;
            const percent = total ? Math.min(99, Math.round((loaded / total) * 100)) : 0;
            setUploadProgress({ fileName: displayName, current: 1, total: 1, percent, dropTarget: target.dropTarget || 'capture', milestoneId: target.milestoneId || null });
          },
        });
        if (uploaded && uploaded.document) rememberDocument(uploaded.document, target);
        setUploadProgress({ fileName: displayName, current: 1, total: 1, percent: 100, dropTarget: target.dropTarget || 'capture', milestoneId: target.milestoneId || null });
      } finally {
        window.setTimeout(() => setUploadProgress(null), 450);
      }
    };
    if (!active) return <Panel title="Context"><span className="muted">No project area.</span></Panel>;
    const projectScoped = !window.AC.projectHasSoftwareFields(project);
    const workPackagesById = new Map((project.workPackages || []).map((workPackage) => [workPackage.id, workPackage]));
    const milestones = (project.milestones || []).map((m) => ({ ...m, workPackageName: (workPackagesById.get(m.workPackageId) || {}).name || 'Project' }));
    const activeMilestones = projectScoped
      ? milestones
      : milestones.filter((m) => m.workPackageId === active.id || (!m.workPackageId && active.position === 0));
    const contextItems = buildProjectContextItems(project, db, recentDocuments);
    const openContextSource = (item) => {
      if (item.type === 'file') onAction('openProjectDocument', { projectId: project.id, documentId: item.id });
      else if (item.type === 'email' && onSelectTab) onSelectTab('email');
      else if (item.type === 'note') onDrawer('note', item.id);
    };
    return (
      <div className="grid ctx-tab">
        {!projectScoped && <WorkPackageScopeBar planning={planning} project={project} onAction={onAction} Inline={Inline} />}
        <div className="ctx-cols">
          <ProjectContextInbox project={project} db={db} items={contextItems} milestones={milestones} filter={filter} setFilter={setFilter} search={search} setSearch={setSearch} needsRouting={needsRouting} setNeedsRouting={setNeedsRouting} actionCenterOverview={actionCenterOverview} bridgeActiveEmployeeIds={bridgeActiveEmployeeIds} bridgeActivityByEmployeeId={bridgeActivityByEmployeeId} bridgeActivityByRunId={bridgeActivityByRunId} onOpenItem={setSelectedContextItem} onDrawer={onDrawer} onAction={onAction} onSelectTab={onSelectTab} onToast={onToast} onError={onError} projectScoped={projectScoped} />
          <div className="ctx-side">
            <ProjectContextCapture project={project} db={db} active={active} milestones={activeMilestones} uploadProgress={uploadProgress} onUploadFiles={uploadFiles} onUploadPackage={uploadPackage} onAction={onAction} defaultOpen={contextItems.length === 0} />
          </div>
        </div>
        <ActionItemDetailModal item={selectedContextItem} db={db} onClose={() => setSelectedContextItem(null)} onOpenSource={openContextSource} />
      </div>
    );
  }

  function ProjectRequirementsTab({ planning, project, db, onDrawer, onAction, onToast, onError, onReload, onVoiceContextChange, Inline }) {
    const { active } = planning;
    if (!active) return <Panel title="Design"><span className="muted">No work packages.</span></Panel>;
    return (
      <div className="grid req-tab">
        <WorkPackageScopeBar planning={planning} project={project} onAction={onAction} Inline={Inline} />
        <RequirementsPanel active={active} planning={planning} project={project} db={db} onDrawer={onDrawer} onAction={onAction} onToast={onToast} onError={onError} onReload={onReload} onVoiceContextChange={onVoiceContextChange} />
      </div>
    );
  }

  function ProjectPlanningExec(props) {
    return <ProjectRequirementsTab {...props} />;
  }

  Object.assign(window, { ProjectPlanningExec, ProjectContextTab, ProjectRequirementsTab, WorkPackageScopeBar, buildProjectContextItems });
})();
